import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { useMutation } from '@apollo/client'
import { useApolloClient } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import { F0Card, F0Dialog, F0Heading, F0Text, Tag } from '@factorialco/f0-react'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { File, FileSigned, Pin } from '@factorialco/f0-react/icons/app'
import {
  AvatarNameSelectorDocument,
  CompanyEmployeesCountsDocument,
  CompanyEmployeesTableDocument,
  CompanyEmployeesTableQuery,
  ItManagementDocumentCountsDocument,
  PendingToSendItAssetDocumentsDocument,
  PendingSignaturesItAssetDocumentsDocument,
  SendForSignatureItAssetDocumentDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { buildUrl } from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUI, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useTeamFilter } from 'modules/employees/lib/filters/team'
import EmployeeDetailsSidePanel from 'modules/it_management/components/EmployeeDetailsSidePanel'
import { useItAssetDocumentUpdated } from 'modules/it_management/hooks/useItAssetDocumentUpdated'
import { useItManagementFeatureAvailability } from 'modules/it_management/hooks/useItManagementFeatureAvailability'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

type CompanyEmployee =
  CompanyEmployeesTableQuery['itManagement']['companyEmployeesConnection']['nodes'][number]

interface SelectedItem {
  item: CompanyEmployee
  checked: boolean
}

interface BulkActionParams {
  itemsStatus: readonly SelectedItem[]
}

const PeoplePage = () => {
  const {
    isItManagementEnabled,
    loading: featureLoading,
    error: featureError,
  } = useItManagementFeatureAvailability()

  const { company, access } = useCurrentSession()
  const client = useApolloClient()
  const teamFilter = useTeamFilter()
  const [sendForSignature] = useMutation(SendForSignatureItAssetDocumentDocument, {
    refetchQueries: [
      { query: CompanyEmployeesTableDocument },
      { query: CompanyEmployeesCountsDocument },
      { query: ItManagementDocumentCountsDocument },
      { query: PendingToSendItAssetDocumentsDocument },
    ],
  })

  const { data: countsData } = useQuery(CompanyEmployeesCountsDocument, {
    fetchPolicy: 'cache-and-network',
  })

  const { data: documentCountsData } = useQuery(ItManagementDocumentCountsDocument, {
    fetchPolicy: 'cache-and-network',
  })

  const [selectedEmployee, setSelectedEmployee] = useState<CompanyEmployee | null>(null)
  const [sendNowModalOpen, setSendNowModalOpen] = useState(false)
  const [viewDetailsModalOpen, setViewDetailsModalOpen] = useState(false)

  const { data: pendingToSendData } = useQuery(PendingToSendItAssetDocumentsDocument, {
    skip: !sendNowModalOpen,
    fetchPolicy: 'cache-and-network',
  })

  const { data: pendingSignaturesData } = useQuery(PendingSignaturesItAssetDocumentsDocument, {
    skip: !viewDetailsModalOpen,
    fetchPolicy: 'cache-and-network',
  })

  const pendingToSendRows = useMemo(() => {
    const nodes = pendingToSendData?.itManagement?.pendingToSendDocuments?.nodes ?? []
    const grouped = new Map<
      string,
      {
        employeeId: number
        documentType: string
        count: number
        employee: (typeof nodes)[number]['employee']
      }
    >()
    for (const node of nodes) {
      const employee = node.employee
      if (!employee) continue
      const key = `${node.employeeId}-${node.documentType}`
      const existing = grouped.get(key)
      if (existing) {
        existing.count += 1
      } else {
        grouped.set(key, {
          employeeId: node.employeeId,
          documentType: node.documentType,
          count: 1,
          employee,
        })
      }
    }
    return Array.from(grouped.values())
  }, [pendingToSendData])

  const pendingToSendEmployeeIds = useMemo(() => {
    const nodes = pendingToSendData?.itManagement?.pendingToSendDocuments?.nodes ?? []
    return Array.from(
      new Set(nodes.map((n) => n.employeeId).filter((id): id is number => id != null))
    )
  }, [pendingToSendData])

  const pendingSignaturesRows = useMemo(() => {
    const nodes = pendingSignaturesData?.itManagement?.pendingSignaturesDocuments?.nodes ?? []
    const grouped = new Map<
      string,
      {
        employeeId: number
        documentType: string
        count: number
        sendDate: string | null
        employee: (typeof nodes)[number]['employee']
      }
    >()
    for (const node of nodes) {
      const employee = node.employee
      if (!employee) continue
      const key = `${node.employeeId}-${node.documentType}`
      const existing = grouped.get(key)
      const sentAt = node.sentAt ?? null
      if (existing) {
        existing.count += 1
        if (sentAt && (!existing.sendDate || sentAt > existing.sendDate)) {
          existing.sendDate = sentAt
        }
      } else {
        grouped.set(key, {
          employeeId: node.employeeId,
          documentType: node.documentType,
          count: 1,
          sendDate: sentAt,
          employee,
        })
      }
    }
    return Array.from(grouped.values())
  }, [pendingSignaturesData])

  const [sendNowLoading, setSendNowLoading] = useState(false)

  useItAssetDocumentUpdated()

  useEffect(() => {
    track(trackingEvents.itManagement.itAssetDocument.goToPeopleTab, {
      company_id: company.id,
      user_id: access.id,
    })
  }, [company.id, access.id])

  const [peopleCounts, setPeopleCounts] = useState({
    all: 0,
    active: 0,
    newHires: 0,
    terminated: 0,
  })

  useEffect(() => {
    if (!countsData) return
    setPeopleCounts({
      all: countsData.itManagement.allCount.totalCount ?? 0,
      active: countsData.itManagement.activeCount.totalCount ?? 0,
      newHires: countsData.itManagement.newHiresCount.totalCount ?? 0,
      terminated: countsData.itManagement.terminatedCount.totalCount ?? 0,
    })
  }, [countsData])

  const handleSendForSignature = useCallback(
    async (employeeIds: number[], origin: '3 dots option' | 'bulk action' | 'card send now') => {
      if (!company?.id || employeeIds.length === 0) return

      try {
        const result = await sendForSignature({
          variables: {
            companyId: Number(company.id),
            employeeIds: employeeIds,
          },
        })

        const itAssetDocuments =
          result.data?.itManagementMutations?.sendForSignatureItmanagementItAssetDocument
            ?.itAssetDocuments || []

        const uniqueDocuments = new Set(
          itAssetDocuments
            .map((doc) => `${doc.employeeId}-${doc.documentType}`)
            .filter((key) => !key.includes('undefined') && !key.includes('null'))
        )

        uniqueDocuments.forEach(() => {
          track(trackingEvents.itManagement.itAssetDocument.sendDocumentForSignature, {
            company_id: company.id,
            user_id: access.id,
            origin,
          })
        })

        app.setFlash({
          type: 'correct',
          message: i18n.t('it_management.people.send_signature.success'),
        })
      } catch {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('it_management.people.send_signature.error'),
        })
      }
    },
    [company?.id, access.id, sendForSignature]
  )

  const handleSendNowConfirm = useCallback(async () => {
    if (pendingToSendEmployeeIds.length === 0) return
    setSendNowLoading(true)
    try {
      await handleSendForSignature(pendingToSendEmployeeIds, 'card send now')
      setSendNowModalOpen(false)
    } finally {
      setSendNowLoading(false)
    }
  }, [pendingToSendEmployeeIds, handleSendForSignature])

  const handleCloseSidePanel = useCallback(() => {
    setSelectedEmployee(null)
  }, [])

  const dataSourceOptions = useMemo(() => {
    const definedFilters = {
      documentsSigned: {
        type: 'in' as const,
        label: i18n.t('it_management.people.filters.documents_signed'),
        options: {
          options: async () => [
            {
              label: i18n.t('it_management.people.filters.documents_signed_options.missing'),
              value: 'missing',
            },
            {
              label: i18n.t('it_management.people.filters.documents_signed_options.complete'),
              value: 'complete',
            },
          ],
        },
      },
      startDate: {
        type: 'in' as const,
        label: i18n.t('it_management.people.filters.start_date'),
        options: {
          options: async () => [
            {
              label: i18n.t('it_management.people.filters.start_date_options.in_30_days'),
              value: 'in_30_days',
            },
            {
              label: i18n.t('it_management.people.filters.start_date_options.started_30_days'),
              value: 'started_30_days',
            },
            {
              label: i18n.t(
                'it_management.people.filters.start_date_options.started_more_than_30_days'
              ),
              value: 'started_more_than_30_days',
            },
          ],
        },
      },
      team: teamFilter,
      manager: {
        type: 'in' as const,
        label: i18n.t('employees.employees_list.filters.reporting_to'),
        options: {
          cache: true,
          options: async () => {
            if (!company?.id) return []

            const { data } = await client.query({
              query: AvatarNameSelectorDocument,
              variables: {
                companyId: Number(company.id),
                includeTeams: false,
                includeWorkplaces: false,
              },
              fetchPolicy: 'cache-first',
            })

            return data.employees.employeeNames.map((employee) => ({
              label: employee.fullName,
              value: employee.id,
            }))
          },
        },
      },
      employeeStatus: {
        type: 'in' as const,
        label: i18n.t('it_management.people.filters.employee_status'),
        options: {
          options: async () => [
            {
              label: i18n.t('it_management.people.presets.active_employees'),
              value: 'active',
            },
            {
              label: i18n.t('it_management.people.presets.new_hires'),
              value: 'newHires',
            },
            {
              label: i18n.t('it_management.people.presets.offboarded'),
              value: 'terminated',
            },
          ],
        },
      },
    }

    return {
      document: CompanyEmployeesTableDocument,
      mapData: (tableData: CompanyEmployeesTableQuery) =>
        tableData.itManagement.companyEmployeesConnection.nodes,
      perPage: 25,
      itemOnClick: (item: CompanyEmployee) => () => {
        setSelectedEmployee(item)
      },
      sortings: {
        employeeName: {
          label: i18n.t('it_management.common.people_title'),
          sortBy: 'employeeName',
        },
        startDate: {
          label: i18n.t('it_management.common.start_date'),
          sortBy: 'startDate',
        },
        workplace: {
          label: i18n.t('it_management.common.workplace'),
          sortBy: 'workplace',
        },
      },
      defaultSortings: {
        field: 'employeeName' as const,
        order: 'asc' as const,
      },
      search: {
        enabled: true,
      },
      filters: definedFilters,
      presets: [
        {
          label: i18n.t('it_management.people.presets.active_employees'),
          filter: {
            employeeStatus: ['active'],
          },
          itemsCount: () => peopleCounts.active,
        },
        {
          label: i18n.t('it_management.people.presets.new_hires'),
          filter: {
            employeeStatus: ['newHires'],
          },
          itemsCount: () => peopleCounts.newHires,
        },
        {
          label: i18n.t('it_management.people.presets.offboarded'),
          filter: {
            employeeStatus: ['terminated'],
          },
          itemsCount: () => peopleCounts.terminated,
        },
      ],
      mapVariables: (variables: {
        search?: string
        sortings?: Array<{ field: string; order: string }>
        filters?: Record<string, unknown>
      }) => {
        const filters = variables.filters || {}

        const employeeStatus: string[] | undefined = Array.isArray(filters.employeeStatus)
          ? filters.employeeStatus
          : undefined
        const onlyActive = employeeStatus?.includes('active') ?? false
        const onlyNewHires = employeeStatus?.includes('newHires') ?? false
        const onlyTerminated = employeeStatus?.includes('terminated') ?? false

        // Only process filters that are defined in the filters definition
        const definedFilterKeys = Object.keys(definedFilters)
        const normalizedFilters: Record<string, unknown> = Object.keys(filters)
          .filter((key) => definedFilterKeys.includes(key))
          .reduce<Record<string, unknown>>((acc, key) => {
            acc[key] = filters[key]
            return acc
          }, {})

        const documentsSigned: string[] | undefined = Array.isArray(
          normalizedFilters.documentsSigned
        )
          ? normalizedFilters.documentsSigned
          : undefined
        const onlyPendingDocuments =
          documentsSigned?.includes('missing') && !documentsSigned?.includes('complete')
            ? true
            : documentsSigned?.includes('complete') && !documentsSigned?.includes('missing')
              ? false
              : undefined

        const startDate: string[] | undefined = Array.isArray(normalizedFilters.startDate)
          ? normalizedFilters.startDate
          : undefined
        const startDateFilter = startDate && startDate.length === 1 ? startDate[0] : undefined

        const teamIds: number[] | undefined = Array.isArray(normalizedFilters.team)
          ? normalizedFilters.team.map((id) => Number(id)).filter((id) => !isNaN(id))
          : undefined

        const managerIds: number[] | undefined = Array.isArray(normalizedFilters.manager)
          ? normalizedFilters.manager.map((id) => Number(id)).filter((id) => !isNaN(id))
          : undefined

        return {
          search: variables.search,
          sort: variables.sortings?.[0],
          onlyActive,
          onlyTerminated,
          onlyNewHires,
          onlyPendingDocuments: onlyPendingDocuments,
          startDateFilter: startDateFilter,
          teamIds: teamIds && teamIds.length > 0 ? teamIds : undefined,
          managerIds: managerIds && managerIds.length > 0 ? managerIds : undefined,
        }
      },
      mapPagination: (tableData: CompanyEmployeesTableQuery) => ({
        total: tableData.itManagement.companyEmployeesConnection.totalCount ?? 0,
      }),
      selectable: (item: CompanyEmployee) => item.id,
      bulkActions: (selectedItems: BulkActionParams) => {
        const selectedCheckedItems = selectedItems.itemsStatus.filter((item) => item.checked)
        const hasDraftDocuments = selectedCheckedItems.some(
          (item) => item.item.hasDraftDocuments ?? false
        )

        if (!hasDraftDocuments || selectedCheckedItems.length === 0) {
          return { primary: [], secondary: [] }
        }

        return {
          primary: [
            {
              id: 'send-it-documents',
              label: i18n.t('it_management.people.bulk_actions.send_it_documents'),
              icon: FileSigned,
            },
          ],
          secondary: [],
        }
      },
      itemActions: (item: CompanyEmployee) => {
        const actions = []

        if (item.hasDraftDocuments ?? false) {
          actions.push({
            label: i18n.t('it_management.people.actions.send_for_signature'),
            icon: FileSigned,
            onClick: () => {
              handleSendForSignature([item.id], '3 dots option')
            },
          })
        }

        return actions
      },
    }
  }, [handleSendForSignature, peopleCounts, client, company?.id, teamFilter])

  const source = useGraphqlDataCollectionSource(dataSourceOptions, [])

  return (
    <>
      <ResponsivePage.Body
        authorized={isItManagementEnabled}
        error={featureError}
        loading={featureLoading}
        variant="full"
      >
        <Box id="it-management-people-data-collection" flexDirection="column" gap="s24">
          <Box flexDirection="row" gap="s16" flexWrap="wrap">
            <Box style={{ flex: '1', minWidth: '0', maxWidth: '370px' }}>
              <F0Card
                compact
                title={i18n.t('it_management.people.it_documents_card.title')}
                metadata={[
                  {
                    icon: File,
                    tooltip: i18n.t('it_management.people.it_documents_card.pending_to_send'),
                    property: {
                      type: 'text',
                      value: `${documentCountsData?.itManagement?.pendingToSendCount?.totalCount ?? 0} ${i18n.t('it_management.people.it_documents_card.pending_to_send')}`,
                    },
                  },
                ]}
                secondaryActions={[
                  {
                    label: i18n.t('it_management.people.it_documents_card.send_now'),
                    onClick: () => setSendNowModalOpen(true),
                  },
                ]}
              />
            </Box>
            <Box style={{ flex: '1', minWidth: '0', maxWidth: '370px' }}>
              <F0Card
                compact
                title={i18n.t('it_management.people.it_documents_card.title')}
                metadata={[
                  {
                    icon: File,
                    tooltip: i18n.t('it_management.people.it_documents_card.pending_signatures'),
                    property: {
                      type: 'text',
                      value: `${documentCountsData?.itManagement?.pendingSignaturesCount?.totalCount ?? 0} ${i18n.t('it_management.people.it_documents_card.pending_signatures')}`,
                    },
                  },
                ]}
                secondaryActions={[
                  {
                    label: i18n.t('it_management.people.it_documents_card.view_details'),
                    onClick: () => setViewDetailsModalOpen(true),
                  },
                ]}
              />
            </Box>
          </Box>

          <OneDataCollection
            source={source}
            emptyStates={{
              'no-data': {
                emoji: '👥',
                title: i18n.t('it_management.people.empty_state.title'),
                description: i18n.t('it_management.people.empty_state.description'),
              },
            }}
            onBulkAction={(action: string, selectedItems: BulkActionParams) => {
              const selectedCheckedItems = selectedItems.itemsStatus.filter((item) => item.checked)
              const employeeIds = selectedCheckedItems.map((itemStatus) => itemStatus.item.id)

              switch (action) {
                case 'send-it-documents':
                  handleSendForSignature(employeeIds, 'bulk action')
                  break
              }
            }}
            visualizations={[
              {
                type: 'table',
                options: {
                  columns: [
                    {
                      label: i18n.t('it_management.common.people_title'),
                      render: (item: CompanyEmployee) => {
                        const firstName = typeof item.firstName === 'string' ? item.firstName : ''
                        const lastName = typeof item.lastName === 'string' ? item.lastName : ''

                        if (
                          (!firstName || firstName === '') &&
                          (!lastName || lastName === '') &&
                          item.employeeName
                        ) {
                          const nameParts = item.employeeName.trim().split(/\s+/)
                          return {
                            type: 'person',
                            value: {
                              firstName: nameParts[0] ?? '',
                              lastName: nameParts.slice(1).join(' ') ?? '',
                              src: buildUrl(item.access?.avatar),
                            },
                          }
                        }

                        return {
                          type: 'person',
                          value: {
                            firstName: firstName ?? '',
                            lastName: lastName ?? '',
                            src: buildUrl(item.access?.avatar),
                          },
                        }
                      },
                      sorting: 'employeeName',
                    },
                    {
                      label: i18n.t('it_management.common.start_date'),
                      render: (item: CompanyEmployee) => ({
                        type: 'text',
                        value: item.startDate ? dateBackendDateTimeToUI(item.startDate) : '-',
                      }),
                      sorting: 'startDate',
                    },
                    {
                      label: i18n.t('it_management.common.workplace'),
                      render: (item: CompanyEmployee) => {
                        if (!item.workplace) {
                          return {
                            type: 'text',
                            value: '-',
                          }
                        }
                        return {
                          type: 'tag',
                          value: {
                            label: item.workplace,
                            icon: Pin,
                          },
                        }
                      },
                      sorting: 'workplace',
                    },
                    {
                      label: i18n.t('it_management.common.total_it_assets'),
                      render: (item: CompanyEmployee) => ({
                        type: 'tag',
                        value: {
                          label: item.totalItAssets.toString(),
                        },
                      }),
                    },
                    {
                      label: i18n.t('it_management.common.pending_signatures'),
                      render: (item: CompanyEmployee) => {
                        const hasDraftDocuments = item.hasDraftDocuments ?? false
                        const hasPendingSignatureDocuments =
                          item.hasPendingSignatureDocuments ?? false
                        const hasCompletedDocuments = item.hasCompletedDocuments ?? false

                        if (hasPendingSignatureDocuments) {
                          return {
                            type: 'status',
                            value: {
                              status: 'warning',
                              label: i18n.t('it_management.common.pending'),
                            },
                          }
                        }

                        if (hasDraftDocuments) {
                          return {
                            type: 'status',
                            value: {
                              status: 'neutral',
                              label: i18n.t('it_management.common.draft'),
                            },
                          }
                        }

                        if (hasCompletedDocuments) {
                          return {
                            type: 'status',
                            value: {
                              status: 'positive',
                              label: i18n.t('it_management.common.completed'),
                            },
                          }
                        }

                        return {
                          type: 'status',
                          value: {
                            label: '—',
                          },
                        }
                      },
                    },
                  ],
                },
              },
            ]}
          />
        </Box>
      </ResponsivePage.Body>

      {selectedEmployee && (
        <EmployeeDetailsSidePanel
          employee={selectedEmployee}
          open={!!selectedEmployee}
          onClose={handleCloseSidePanel}
        />
      )}

      <F0Dialog
        isOpen={sendNowModalOpen}
        onClose={() => setSendNowModalOpen(false)}
        width="md"
        title={i18n.t('it_management.people.it_documents_card.send_now_modal_title')}
        primaryAction={{
          label: i18n.t('it_management.people.it_documents_card.send'),
          onClick: handleSendNowConfirm,
          isLoading: sendNowLoading,
          isDisabled: pendingToSendEmployeeIds.length === 0,
        }}
        secondaryAction={{
          label: i18n.t('it_management.people.it_documents_card.cancel'),
          onClick: () => setSendNowModalOpen(false),
        }}
      >
        <Box flexDirection="column" gap="s8" paddingTop="s16">
          <F0Heading
            variant="heading"
            content={i18n.t('it_management.people.it_documents_card.pending_documents_summary', {
              count: String(
                pendingToSendData?.itManagement?.pendingToSendDocuments?.totalCount ?? 0
              ),
            })}
          />
          <F0Text
            variant="body"
            color="secondary"
            content={i18n.t('it_management.people.it_documents_card.send_confirmation', {
              count: String(pendingToSendEmployeeIds.length),
            })}
          />
          <Box
            flexDirection="column"
            gap="s8"
            style={{
              maxHeight: '320px',
              overflowY: 'auto',
              border: '1px solid var(--color-grey300, #e5e7eb)',
              borderRadius: '8px',
              padding: '12px',
            }}
          >
            <Box
              flexDirection="row"
              gap="s12"
              style={{
                paddingBottom: '8px',
                borderBottom: '1px solid var(--color-grey300, #e5e7eb)',
                fontWeight: '600',
                fontSize: '12px',
                color: 'var(--color-grey600, #6b7280)',
              }}
            >
              <Box style={{ flex: '1', minWidth: '0' }}>
                {i18n.t('it_management.common.people_title')}
              </Box>
              <Box style={{ flex: '0 0 120px' }}>
                {i18n.t('it_management.people.filters.employee_status')}
              </Box>
              <Box style={{ flex: '0 0 140px' }}>
                {i18n.t('it_management.people.it_documents_card.document_to_send')}
              </Box>
            </Box>
            {pendingToSendRows.map((row) => {
              const name =
                [row.employee?.firstName, row.employee?.lastName]
                  .filter(Boolean)
                  .join(' ')
                  .trim() || '—'
              const statusLabel = row.employee?.active
                ? i18n.t('it_management.people.it_documents_card.employee_status_active')
                : i18n.t('it_management.people.it_documents_card.employee_status_offboarded')
              const statusVariant = row.employee?.active ? ('info' as const) : ('neutral' as const)
              const documentLabel =
                row.documentType === 'assign'
                  ? row.count === 1
                    ? i18n.t('it_management.people.it_documents_card.assign_devices', {
                        count: '1',
                      })
                    : i18n.t('it_management.people.it_documents_card.assign_devices_other', {
                        count: String(row.count),
                      })
                  : row.count === 1
                    ? i18n.t('it_management.people.it_documents_card.retire_devices', {
                        count: '1',
                      })
                    : i18n.t('it_management.people.it_documents_card.retire_devices_other', {
                        count: String(row.count),
                      })
              return (
                <Box
                  key={`${row.employeeId}-${row.documentType}`}
                  flexDirection="row"
                  gap="s12"
                  alignItems="center"
                  style={{
                    padding: '8px 0',
                    borderBottom: '1px solid var(--color-grey200, #f3f4f6)',
                    fontSize: '14px',
                  }}
                >
                  <Box
                    flexDirection="row"
                    alignItems="center"
                    gap="s8"
                    style={{ flex: '1', minWidth: '0' }}
                  >
                    {row.employee?.access?.avatar &&
                      typeof row.employee.access.avatar === 'string' && (
                        <Box
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            backgroundImage: `url(${buildUrl(row.employee.access.avatar)})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            flexShrink: '0',
                          }}
                        />
                      )}
                    <F0Text variant="body" content={name} />
                  </Box>
                  <Box style={{ flex: '0 0 120px' }}>
                    <Tag
                      tag={{
                        type: 'status',
                        text: statusLabel,
                        variant: statusVariant,
                      }}
                    />
                  </Box>
                  <Box
                    flexDirection="row"
                    alignItems="center"
                    gap="s4"
                    style={{ flex: '0 0 140px' }}
                  >
                    <F0Text variant="body" content={documentLabel} />
                  </Box>
                </Box>
              )
            })}
          </Box>
        </Box>
      </F0Dialog>

      <F0Dialog
        isOpen={viewDetailsModalOpen}
        onClose={() => setViewDetailsModalOpen(false)}
        width="md"
        title={i18n.t('it_management.people.it_documents_card.view_details_modal_title')}
        primaryAction={{
          label: i18n.t('common.close'),
          onClick: () => setViewDetailsModalOpen(false),
        }}
      >
        <Box flexDirection="column" gap="s8" paddingTop="s16">
          <F0Heading
            variant="heading"
            content={i18n.t('it_management.people.it_documents_card.view_details_summary', {
              count: String(
                pendingSignaturesData?.itManagement?.pendingSignaturesDocuments?.totalCount ?? 0
              ),
            })}
          />
          <F0Text
            variant="body"
            color="secondary"
            content={i18n.t('it_management.people.it_documents_card.view_details_description')}
          />
          <Box
            flexDirection="column"
            gap="s8"
            style={{
              maxHeight: '320px',
              overflowY: 'auto',
              border: '1px solid var(--color-grey300, #e5e7eb)',
              borderRadius: '8px',
              padding: '12px',
            }}
          >
            <Box
              flexDirection="row"
              gap="s12"
              style={{
                paddingBottom: '8px',
                borderBottom: '1px solid var(--color-grey300, #e5e7eb)',
                fontWeight: '600',
                fontSize: '12px',
                color: 'var(--color-grey600, #6b7280)',
              }}
            >
              <Box style={{ flex: '1', minWidth: '0' }}>
                {i18n.t('it_management.common.people_title')}
              </Box>
              <Box style={{ flex: '0 0 120px' }}>
                {i18n.t('it_management.people.it_documents_card.send_date')}
              </Box>
              <Box style={{ flex: '0 0 140px' }}>
                {i18n.t('it_management.people.it_documents_card.document')}
              </Box>
            </Box>
            {pendingSignaturesRows.map((row) => {
              const name =
                [row.employee?.firstName, row.employee?.lastName]
                  .filter(Boolean)
                  .join(' ')
                  .trim() || '—'
              const sendDateLabel = row.sendDate
                ? stringToDate(row.sendDate, 'backend_datetime').format('DD MMM YYYY')
                : '—'
              const documentLabel =
                row.documentType === 'assign'
                  ? row.count === 1
                    ? i18n.t('it_management.people.it_documents_card.assign_devices', {
                        count: '1',
                      })
                    : i18n.t('it_management.people.it_documents_card.assign_devices_other', {
                        count: String(row.count),
                      })
                  : row.count === 1
                    ? i18n.t('it_management.people.it_documents_card.retire_devices', {
                        count: '1',
                      })
                    : i18n.t('it_management.people.it_documents_card.retire_devices_other', {
                        count: String(row.count),
                      })
              return (
                <Box
                  key={`${row.employeeId}-${row.documentType}`}
                  flexDirection="row"
                  gap="s12"
                  alignItems="center"
                  style={{
                    padding: '8px 0',
                    borderBottom: '1px solid var(--color-grey200, #f3f4f6)',
                    fontSize: '14px',
                  }}
                >
                  <Box
                    flexDirection="row"
                    alignItems="center"
                    gap="s8"
                    style={{ flex: '1', minWidth: '0' }}
                  >
                    {row.employee?.access?.avatar &&
                      typeof row.employee.access.avatar === 'string' && (
                        <Box
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            backgroundImage: `url(${buildUrl(row.employee.access.avatar)})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            flexShrink: '0',
                          }}
                        />
                      )}
                    <F0Text variant="body" content={name} />
                  </Box>
                  <Box style={{ flex: '0 0 120px' }}>
                    <F0Text variant="body" content={sendDateLabel} />
                  </Box>
                  <Box style={{ flex: '0 0 140px' }}>
                    <Tag
                      tag={{
                        type: 'raw',
                        text: documentLabel,
                        icon: File,
                      }}
                    />
                  </Box>
                </Box>
              )
            })}
          </Box>
        </Box>
      </F0Dialog>
    </>
  )
}

export default PeoplePage
